// Phase 1C FINAL — refresh cycle coordinator.
//
// A "refresh cycle" tracks a logical batch of tile requests. It settles when
// every request in the batch has completed (success/error/abort). Only AFTER
// settlement can a targeted skew retry be claimed — at most one per cycle.
//
// A cycle begins ONLY for:
//   - initial viewer load
//   - stats indexed_through_block advancement
//   - visibility/focus refresh discovering stale data
//   - explicit refresh-all (future)
// Camera motion MUST NOT begin a new cycle (it schedules ad-hoc tile refreshes
// via a separate debounced path that does NOT reset the skew budget).
export class RefreshCycle {
  private cycleId = 0;
  private inflight = new Set<string>();
  private settled = false;
  private skewClaimed = false;
  private onSettleCbs: (() => void)[] = [];

  /** Start a new cycle. Any pending settlement callbacks are dropped. */
  beginCycle(): number {
    this.cycleId += 1;
    this.inflight.clear();
    this.settled = false;
    this.skewClaimed = false;
    this.onSettleCbs = [];
    return this.cycleId;
  }

  /** Track a request as part of the current cycle. Ignored if already settled. */
  addRequest(key: string): void {
    if (this.settled) return;
    this.inflight.add(key);
  }

  /**
   * Mark a request as done (success/error/abort all count). When the last
   * tracked request finishes, callbacks registered via onSettle() fire.
   */
  settleRequest(key: string): void {
    if (this.settled) return;
    this.inflight.delete(key);
    if (this.inflight.size === 0) {
      this.settled = true;
      const cbs = this.onSettleCbs;
      this.onSettleCbs = [];
      for (const cb of cbs) cb();
    }
  }

  /** Register a callback fired once when (or as soon as) the batch settles. */
  onSettle(cb: () => void): void {
    if (this.settled) {
      cb();
      return;
    }
    this.onSettleCbs.push(cb);
  }

  /**
   * Try to claim the ONE skew retry allowed for this cycle. Only succeeds
   * AFTER settlement. Range oscillation inside a cycle cannot spend more
   * than one retry.
   */
  claimSkewRetry(): boolean {
    if (!this.settled) return false;
    if (this.skewClaimed) return false;
    this.skewClaimed = true;
    return true;
  }

  get id(): number {
    return this.cycleId;
  }
  get isSettled(): boolean {
    return this.settled;
  }
  get skewUsed(): boolean {
    return this.skewClaimed;
  }
  get pending(): number {
    return this.inflight.size;
  }
}
